import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf/dist/esm/entry.webpack";
import { useLocation } from "react-router-dom";
import images from "../../../../../utilities/images/images";
import ControlPanel from "./ControlPanel";
import "./notes_feature_content_component.scss";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const INITIAL_VISIBLE_PAGES = 2;
const PAGE_PREFETCH_PX = 800;

const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const getNotesFileName = (notesUrl) => {
  if (!notesUrl) return null;
  const match = String(notesUrl).match(/\/([^/]+)$/);
  return match ? match[1] : null;
};

const PdfPageSkeleton = ({ width, scale = 1 }) => {
  const skeletonWidth = width || 720;
  return (
    <div
      className="pdf-page-skeleton"
      style={{
        width: skeletonWidth,
        height: Math.round(skeletonWidth * 1.414 * (scale || 1)),
      }}
      aria-hidden="true"
    />
  );
};

const LazyPdfPage = ({
  pageNumber,
  width,
  scale,
  scrollRoot,
  forceRender,
  onFirstPageReady,
}) => {
  const slotRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(
    forceRender || pageNumber <= INITIAL_VISIBLE_PAGES
  );

  useEffect(() => {
    if (shouldRender) return undefined;
    const node = slotRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      {
        root: scrollRoot || null,
        rootMargin: `${PAGE_PREFETCH_PX}px 0px`,
        threshold: 0.01,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldRender, scrollRoot]);

  return (
    <div ref={slotRef} className="pdf-page-slot">
      {shouldRender ? (
        <Page
          pageNumber={pageNumber}
          width={width}
          scale={scale}
          renderAnnotationLayer={false}
          onRenderSuccess={() => {
            if (pageNumber === 1 && onFirstPageReady) {
              onFirstPageReady();
            }
          }}
          onRenderError={() => {
            if (pageNumber === 1 && onFirstPageReady) {
              onFirstPageReady();
            }
          }}
          loading={<PdfPageSkeleton width={width} scale={scale} />}
        />
      ) : (
        <PdfPageSkeleton width={width} scale={scale} />
      )}
    </div>
  );
};

const NotesPdfLoader = ({
  title,
  phase,
  progress,
  loadedBytes,
  totalBytes,
}) => {
  const hasDeterminateProgress = phase === "downloading" && totalBytes > 0;
  const displayProgress = hasDeterminateProgress
    ? progress
    : phase === "opening" || phase === "rendering"
    ? 100
    : 0;

  const statusText =
    phase === "downloading"
      ? "Downloading notes"
      : phase === "opening"
      ? "Opening document"
      : "Preparing first page";

  const detailText = hasDeterminateProgress
    ? `${formatBytes(loadedBytes)} of ${formatBytes(totalBytes)}`
    : phase === "downloading" && loadedBytes
    ? `${formatBytes(loadedBytes)} downloaded`
    : "This may take a moment for larger files";

  return (
    <div className="notes-pdf-loader" role="status" aria-live="polite">
      <div className="notes-pdf-loader__card">
        <div className="notes-pdf-loader__icon" aria-hidden="true">
          <span className="notes-pdf-loader__spinner" />
        </div>
        <p className="notes-pdf-loader__status">{statusText}</p>
        {title ? <p className="notes-pdf-loader__title">{title}</p> : null}
        <div
          className={`notes-pdf-progress${
            hasDeterminateProgress ? "" : " notes-pdf-progress--indeterminate"
          }`}
        >
          <div
            className="notes-pdf-progress__fill"
            style={
              hasDeterminateProgress
                ? { width: `${displayProgress}%` }
                : undefined
            }
          />
        </div>
        <div className="notes-pdf-loader__meta">
          {hasDeterminateProgress ? (
            <span>{displayProgress}%</span>
          ) : (
            <span>Working…</span>
          )}
          <span>{detailText}</span>
        </div>
      </div>
      <div className="notes-pdf-loader__preview" aria-hidden="true" />
    </div>
  );
};

const NotesFeatureContentComponent = ({
  history,
  notesData,
  tokenType,
  token,
}) => {
  const location = useLocation();
  const scrollRef = useRef(null);
  const firstPageReadyRef = useRef(false);

  const [numPages, setNumPages] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [scale, setScale] = useState(1.5);
  const [pageWidth, setPageWidth] = useState(
    Math.min(typeof window !== "undefined" ? window.innerWidth - 40 : 800, 800)
  );
  const [phase, setPhase] = useState("downloading");
  const [progress, setProgress] = useState(0);
  const [loadedBytes, setLoadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [scrollRoot, setScrollRoot] = useState(null);

  useEffect(() => {
    setScrollRoot(scrollRef.current);
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let cancelled = false;

    const resetViewer = () => {
      firstPageReadyRef.current = false;
      setPdfData(null);
      setNumPages(null);
      setProgress(0);
      setLoadedBytes(0);
      setTotalBytes(0);
      setErrorMessage("");
      setPhase("downloading");
    };

    const getPdfData = async () => {
      resetViewer();

      const fileName = getNotesFileName(notesData?.notes);
      if (!fileName || !notesData?.notes_id || !token) {
        setErrorMessage("This note could not be opened. Please go back and try again.");
        setPhase("error");
        return;
      }

      try {
        const response = await axios.get(
          `https://learning.motion.ac.in/motioneducation/api/notes?file=${fileName}&id=${notesData.notes_id}`,
          {
            headers: {
              Authorization: `${tokenType} ${token}`,
            },
            responseType: "arraybuffer",
            cancelToken: source.token,
            onDownloadProgress: (event) => {
              if (cancelled) return;
              const loaded = event.loaded || 0;
              const total = event.total || 0;
              setLoadedBytes(loaded);
              if (total > 0) {
                setTotalBytes(total);
                setProgress(Math.min(100, Math.round((loaded / total) * 100)));
              }
            },
          }
        );

        if (cancelled) return;

        if (response.status === 200 && response.data) {
          setPdfData(response.data);
          setProgress(100);
          setPhase("opening");
          return;
        }

        throw new Error("Unable to download notes");
      } catch (error) {
        if (cancelled || axios.isCancel(error)) return;
        console.error("Error fetching PDF:", error);
        setErrorMessage(
          "We could not load these notes. Check your connection and try again."
        );
        setPhase("error");
      }
    };

    getPdfData();

    return () => {
      cancelled = true;
      source.cancel("Notes request cancelled");
    };
  }, [notesData?.notes, notesData?.notes_id, token, tokenType, retryCount]);

  useEffect(() => {
    const calculatePageWidth = () => {
      const maxWidth = Math.min(window.innerWidth - 40, 800);
      setPageWidth(maxWidth);
    };

    calculatePageWidth();
    window.addEventListener("resize", calculatePageWidth);

    return () => window.removeEventListener("resize", calculatePageWidth);
  }, []);

  useEffect(() => {
    const updateScale = () => {
      if (window.innerWidth >= 1024) {
        setScale(1.5);
      } else if (window.innerWidth >= 768 && window.innerWidth < 1024) {
        setScale(1.2);
      } else {
        setScale(1);
      }
    };

    window.addEventListener("resize", updateScale);
    updateScale();

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  const handleRedirect = () => {
    const returnState = location.state;
    const topicState =
      returnState?.returnToTopic ||
      (returnState?.featureType != null ? returnState : null);

    if (topicState) {
      history.push({
        pathname: "/topic_component",
        state: {
          ...topicState,
          screenVersion: "new",
          fromSubject: false,
        },
      });
      return;
    }

    history.push({
      pathname: "/dashboard/student-learning",
      state: {
        screenVersion: "new",
        returnTo: "/dashboard",
      },
    });
  };

  const handleZoomIn = () => {
    setScale((prevScale) => prevScale * 1.2);
  };

  const handleZoomOut = () => {
    setScale((prevScale) => prevScale / 1.2);
  };

  const handleDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    setPhase((current) => (current === "ready" ? current : "rendering"));
  };

  const handleFirstPageReady = useCallback(() => {
    if (firstPageReadyRef.current) return;
    firstPageReadyRef.current = true;
    setPhase("ready");
  }, []);

  useEffect(() => {
    if (phase !== "rendering") return undefined;
    const timeoutId = setTimeout(() => {
      if (!firstPageReadyRef.current) {
        firstPageReadyRef.current = true;
        setPhase("ready");
      }
    }, 8000);
    return () => clearTimeout(timeoutId);
  }, [phase]);

  const handleRetry = () => {
    setRetryCount((count) => count + 1);
  };

  const isBusy = phase === "downloading" || phase === "opening" || phase === "rendering";
  const showViewer = Boolean(pdfData);

  return (
    <Fragment>
      <div className="notesFeatureContentComponent_wrapper">
        <div className="pdf-container">
          <div
            ref={scrollRef}
            className="notesFeatureContentComponent_inner_wrapper"
          >
            <div className="notes-sticky-toolbar">
              <div className="pdf-header">
                <div className="btn_wrapper">
                  <button onClick={handleRedirect}>
                    <img src={images.user_activity_1} alt="Back" />
                  </button>
                </div>
                <div className="headingContentWrapper">
                  <div className="titleText">{notesData?.notes_title}</div>
                  {phase === "ready" && numPages ? (
                    <div className="notes-page-count">
                      {numPages} {numPages === 1 ? "page" : "pages"}
                    </div>
                  ) : null}
                </div>
              </div>
              {showViewer ? (
                <ControlPanel
                  zoomIn={handleZoomIn}
                  zoomOut={handleZoomOut}
                  setScale={setScale}
                  scale={scale}
                  pdfData={pdfData}
                  notesData={notesData}
                />
              ) : null}
            </div>

            <div className="notes_wrapper">
              {isBusy ? (
                <NotesPdfLoader
                  title={notesData?.notes_title}
                  phase={phase}
                  progress={progress}
                  loadedBytes={loadedBytes}
                  totalBytes={totalBytes}
                />
              ) : null}

              {phase === "error" ? (
                <div className="notes-pdf-error">
                  <p>{errorMessage}</p>
                  <button type="button" onClick={handleRetry}>
                    Try again
                  </button>
                </div>
              ) : null}

              {showViewer ? (
                <div
                  id="pdf-div"
                  className={`notes_inner_wrapper notes-pdf-stage${
                    phase === "ready" ? " is-ready" : ""
                  }`}
                >
                  <div className="pdf-container">
                    <Document
                      file={{ data: pdfData }}
                      onLoadSuccess={handleDocumentLoadSuccess}
                      onLoadError={() => {
                        setErrorMessage(
                          "The document could not be opened. Please try again."
                        );
                        setPhase("error");
                      }}
                      onContextMenu={(e) => e.preventDefault()}
                      renderMode="canvas"
                      loading={null}
                      error={null}
                    >
                      {numPages
                        ? Array.from(
                            new Array(phase === "ready" ? numPages : 1),
                            (_, index) => (
                              <LazyPdfPage
                                key={`page_${index + 1}`}
                                pageNumber={index + 1}
                                width={pageWidth}
                                scale={scale}
                                scrollRoot={scrollRoot}
                                forceRender={index < INITIAL_VISIBLE_PAGES}
                                onFirstPageReady={handleFirstPageReady}
                              />
                            )
                          )
                        : null}
                    </Document>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default NotesFeatureContentComponent;
